/**
 * StickyTabs - Classic layout sticky tabs section
 *
 * Renders the sticky tabs with CTA button, tab navigation, and header action.
 * This is Classic-specific (Attraction has its own StickyTabs without CTA).
 */
import { useMemo, useState, useEffect } from "react";
import { useRouter } from "next/router";
import { useTranslation } from "@shared/utils/internationalization";
import moment from "moment-timezone";
import SwiperCore, { Navigation } from "swiper";
import { Swiper, SwiperSlide } from "swiper/react";
import "swiper/swiper-bundle.min.css";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Box from "@ui-kit/layout/Box/Box.web";
import Grid from "@ui-kit/layout/Grid/Grid.web";
import GridItem from "@ui-kit/layout/GridItem/GridItem.web";
import Text from "@ui-kit/typography/Text/Text.web";
import { StickySection } from "@shared/components/application/layout/StickySection/StickySection.web";
import { common } from "@shared/constants";
import breakpoints from "@shared/theme/props/breakpoints";
import { gridTemplates, gridSpans } from "@shared/theme/props/grid";
import { usePublicBasket } from "@app-contexts/public/features/basket/services/usePublicBasket";
import useDetailPageTabHeight from "@app-contexts/public/features/event-detail/hooks/useDetailPageTabHeight";
import { useMediaQuery } from "@shared/hooks/useMediaQuery";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Icon from "@ui-kit/icons/Icon.web";
import { getPriceRange } from "@shared/modules/financials/utils/price-utils";
import { useTimer } from "@ui-kit/feedback/timer/useTimer";
import { SITE_URL } from "@shared/env";
import CartSummary from "@app-contexts/public/features/basket/components/cart-summary/CartSummary/CartSummary.web";
import { TabBar as Tabs } from "@ui-kit/disclosure/TabBar/TabBar.web";
import { useShowHeaderActionInfo } from "../../../hooks/useShowHeaderActionInfo";
import { DetailPageButton } from "../../../components/shared/DetailPageButton.web";
import { HeaderShareLinks } from "../../../components/shared/HeaderShareLinks.web";
import type { PageData } from "../../../adapters/types";
import { swiperContainerStyles } from "../../shared-styles";

const { xs, sm, md } = breakpoints;
const { headerTabsId } = common.domElementIds;

interface TabButtonProps {
  isSelected: boolean;
  onClick: () => void;
  title: string;
}

const TabButton = ({ isSelected, onClick, title }: TabButtonProps) => (
  <Box
    as="button"
    role="button"
    tabIndex={0}
    onClick={onClick}
    data-testid={`${title}-tab`}
    bg="transparent"
    border="none"
    p="none"
    pb="sm"
    mx="md"
    fontWeight="normal"
    cursor="pointer"
    borderBottom={isSelected ? "3px solid" : "3px solid transparent"}
    borderBottomColor={isSelected ? "primary.solid" : "transparent"}
    _hover={{
      bg: "transparent",
    }}
    _focusVisible={{
      outline: "2px solid",
      outlineColor: "blue.focusRing",
      borderRadius: "1px",
    }}
    // eslint-disable-next-line showpass-custom/prefer-semantic-props -- Dynamic breakpoint requires css prop
    css={{
      [`@media (min-width: ${sm}px)`]: {
        "&:first-of-type": {
          marginLeft: 0,
        },
      },
    }}
  >
    <Text fontSize={{ base: "xs", sm: "sm" }} color="fg">
      {title}
    </Text>
  </Box>
);

interface StickyTabsProps {
  data: PageData;
}

export function StickyTabs({ data }: StickyTabsProps) {
  const { t } = useTranslation("purchases");
  const router = useRouter();
  const { hasItems } = usePublicBasket();
  const tabHeight = useDetailPageTabHeight(hasItems);
  const showHeaderActionInfo = useShowHeaderActionInfo();
  const [isMobile] = useMediaQuery(`(max-width: ${xs - 1}px)`);
  const [isTabletMedia] = useMediaQuery(
    `(min-width: ${xs}px) and (max-width: ${md - 1}px)`
  );

  const { stickyTabs, ui, rawData, pageType, isAttraction } = data;

  SwiperCore.use([Navigation]);
  const [swiper, setSwiper] = useState<SwiperCore | undefined>();

  const visibleTabs = stickyTabs.tabs.filter((tab) => tab.visible);

  const handleTabClick = (tabId: number, index: number) => {
    const tabInfo = visibleTabs.find((tab) => tab.id === tabId);

    if (tabInfo?.hash) {
      stickyTabs.onTabChange(tabId);
      router.replace({ hash: tabInfo.hash }, undefined, { shallow: true });

      if ((isMobile || isTabletMedia) && visibleTabs.length > 2) {
        if (swiper && index === 1) {
          swiper.slideTo(0);
        } else if (swiper && index >= 2) {
          swiper.slideTo(index - 2);
        }
      }
    } else if (tabInfo?.onClick) {
      tabInfo.onClick();
    }
  };

  // Price range for CTA - works for both events and memberships
  const priceRangeString = useMemo(() => {
    if (pageType === "event" && rawData.event) {
      return rawData.event.no_ticket_types
        ? t("Free")
        : getPriceRange({
            items: rawData.event.ticket_types,
            allInPricing: Boolean(
              rawData.event.venue?.all_in_pricing_is_enabled
            ),
            venueCurrency: rawData.event.venue?.currency ?? "CAD",
            shouldRound: false,
          });
    }

    if (pageType === "membership" && rawData.membership) {
      return getPriceRange({
        items: rawData.membership.membership_levels,
        allInPricing: Boolean(
          rawData.membership.venue?.all_in_pricing_is_enabled
        ),
        venueCurrency: rawData.membership.venue?.currency ?? "CAD",
        shouldRound: false,
      });
    }

    return "";
  }, [rawData.event, rawData.membership, pageType, t]);

  // Timer for sale start
  const eventSaleStartsOn = rawData.event?.event_sale_starts_on;
  const saleStartsOnTz =
    eventSaleStartsOn && rawData.event?.timezone
      ? moment.tz(eventSaleStartsOn, rawData.event.timezone).toDate()
      : null;
  const [timeRemaining] = useTimer(saleStartsOnTz ?? new Date());

  // Hydration-safe date comparison - server renders as true to avoid mismatch
  const [saleStartsOnPassed, setSaleStartsOnPassed] = useState(true);
  useEffect(() => {
    if (eventSaleStartsOn) {
      setSaleStartsOnPassed(
        Date.now() >= new Date(eventSaleStartsOn).valueOf()
      );
    }
  }, [eventSaleStartsOn]);

  // Share links data
  const shareButtonsData = useMemo(() => {
    if (rawData.event) {
      return {
        url: `${SITE_URL}/${rawData.event.slug}`,
        description: rawData.event.description || "",
      };
    }
    if (rawData.membership) {
      return {
        url: `${SITE_URL}/pass/${rawData.membership.slug}`,
        description: rawData.membership.description || "",
      };
    }
    return { url: SITE_URL, description: "" };
  }, [rawData.event, rawData.membership]);

  const hidePriceText = !priceRangeString || isAttraction;

  // Date/time text for events only
  const getStartsOnText = () => {
    if (pageType !== "event" || !rawData.event) {
      return undefined;
    }
    if (rawData.event.date_time_to_be_determined) {
      return t("Date and Time TBD");
    }
    if (rawData.event.starts_on) {
      return moment(rawData.event.starts_on)
        .tz(rawData.event.timezone)
        .format("ddd MMM D, YYYY | h:mm A z");
    }
    return undefined;
  };
  const startsOnText = getStartsOnText();

  // Check if this is a free item (no ticket types for events, or free for memberships)
  const isFreeItem =
    pageType === "event" ? rawData.event?.no_ticket_types : false;

  if (stickyTabs.selectedTab === undefined) return null;

  return (
    <StickySection
      top={tabHeight}
      bg="bg"
      zIndex="navbarTabs"
      borderBottom={!ui.isReactNativeApp ? "1px solid" : "0px solid"}
      borderBottomColor="border"
      hidden={stickyTabs.selectedTab === undefined}
      noPadding
    >
      {/* Mobile CTA - show for non-attraction pages */}
      {!isAttraction && (
        <Flex
          display={{ base: "flex", lg: "none" }}
          direction="column"
          borderBottom="1px solid"
          borderBottomColor="border"
          w="100%"
          h="auto"
          pb="env(safe-area-inset-bottom)"
          position="sticky"
          zIndex="var(--z-index-sticky)"
          bottom="none"
        >
          <Flex
            direction="row"
            p={{ base: "10px 20px", sm: "10px 40px" }}
            justifyContent="space-between"
            alignContent="center"
            alignItems="center"
            hidden={isFreeItem}
            display={
              pageType === "event" && eventSaleStartsOn && timeRemaining > 0
                ? "none"
                : "flex"
            }
          >
            <Flex
              direction="column"
              data-testid="price-range"
              display={{ base: "none", sm: "flex" }}
            >
              <Text hidden={hidePriceText} fontSize="xs" color="fg.muted">
                {t("Price")}
              </Text>
              <Text
                hidden={hidePriceText}
                fontSize="sm"
                fontWeight="bold"
                color="fg"
              >
                {priceRangeString}
              </Text>
            </Flex>
            <DetailPageButton
              saleStartsOnPassed={saleStartsOnPassed}
              eventData={rawData.event}
              membershipGroupData={rawData.membership}
              openModal={stickyTabs.cta.onClick}
              props={{
                "data-testid": "call-to-action-button",
                colorPalette: "secondary",
                minHeight: "50px",
                width: { base: "100%", sm: "50%" },
                maxWidth: { base: "100%", sm: "364px" },
                display:
                  pageType === "event" && eventSaleStartsOn && timeRemaining > 0
                    ? "none"
                    : "block",
              }}
            />
          </Flex>
        </Flex>
      )}

      {/* Tab navigation */}
      <Grid h="full" width="full" templateColumns={gridTemplates.default}>
        <GridItem id={headerTabsId} colSpan={gridSpans.main}>
          <Flex bg="bg" w="100%" height="element.4xl">
            <Tabs
              width="100%"
              display="flex"
              flexDirection="row"
              justifyContent="space-between"
              alignItems="center"
              isManual
              index={stickyTabs.selectedTab}
              pl={{ sm: "3xl" }}
              css={swiperContainerStyles}
            >
              <IconButton
                iconName="ChevronLeft"
                iconOutline={false}
                iconSize="20px"
                aria-label="Previous tab"
                className="prev-button"
                iconColor="fg"
                marginLeft={{ base: "none", sm: "-10px" }}
                data-testid="prev-nav-button"
                display={swiper ? undefined : "none"}
                _disabled={{ display: "none" }}
              />
              <Swiper
                navigation={{
                  prevEl: ".prev-button",
                  nextEl: ".next-button",
                }}
                allowTouchMove={visibleTabs.length > 3}
                slidesPerView="auto"
                onSwiper={setSwiper}
                initialSlide={
                  stickyTabs.selectedTab === 0 ? 0 : stickyTabs.selectedTab
                }
                watchOverflow
              >
                {visibleTabs.map((tab, index) => (
                  <SwiperSlide key={tab.title}>
                    <Flex alignItems="flex-end" h="element.4xl">
                      <TabButton
                        isSelected={stickyTabs.selectedTab === tab.id}
                        onClick={() => handleTabClick(tab.id, index)}
                        title={tab.title}
                      />
                    </Flex>
                  </SwiperSlide>
                ))}
              </Swiper>
              <IconButton
                iconName="ChevronRight"
                iconOutline={false}
                iconSize="20px"
                aria-label="Next tab"
                iconColor="fg"
                className="next-button"
                data-testid="next-nav-button"
                display={swiper ? undefined : "none"}
                _disabled={{ display: "none" }}
              />
            </Tabs>
          </Flex>
        </GridItem>

        {/* Desktop header action */}
        <GridItem
          h={0}
          pl="xs"
          pr="3xl"
          colSpan={gridSpans.sidebar}
          display={{ base: "none", lg: "block" }}
        >
          <Flex
            boxShadow="md"
            bg="bg"
            direction="column"
            border="1px solid"
            borderColor="border"
            overflow="hidden"
            borderRadius="md"
          >
            <Flex
              p="0px 24px"
              h="element.4xl"
              borderBottom="1px solid"
              borderBottomColor="border"
              justifyContent="space-between"
              alignItems="center"
              fontSize="sm"
              data-testid="price-range"
            >
              <Text hidden={hidePriceText} color="fg">
                {t("Price")}
              </Text>
              <Text hidden={hidePriceText} fontWeight="bold" color="fg">
                {priceRangeString}
              </Text>
            </Flex>

            {isAttraction ? (
              <Flex p="xl" width="100%" direction="column">
                <CartSummary isDrawer={false} venue={rawData.event?.venue} />
              </Flex>
            ) : (
              <Flex
                p="xl"
                width="100%"
                direction="column"
                data-testid="buy-button-container"
                hidden={isFreeItem}
                display={
                  pageType === "event" && eventSaleStartsOn && timeRemaining > 0
                    ? "none"
                    : "flex"
                }
              >
                <Flex direction="column" mb="lg" hidden={!showHeaderActionInfo}>
                  <Text
                    fontWeight="bold"
                    color="fg"
                    data-testid="title-on-scroll-down"
                  >
                    {stickyTabs.headerAction.title}
                  </Text>
                  {pageType === "event" && (
                    <>
                      <Flex
                        hidden={!startsOnText}
                        alignItems="center"
                        mt="xs"
                        fontSize="sm"
                        data-testid="date-and-time-on-scroll-down"
                      >
                        <Icon.Calendar
                          outline
                          boxSize="element.md"
                          mr="xs"
                          color="fg.muted"
                        />
                        <Text color="fg.muted">{startsOnText}</Text>
                      </Flex>
                      <Flex
                        hidden={!stickyTabs.headerAction.locationText}
                        alignItems="center"
                        mt="2xs"
                        fontSize="sm"
                        data-testid="location-on-scroll-down"
                      >
                        <Icon.Location
                          outline
                          boxSize="element.md"
                          mr="xs"
                          color="fg.muted"
                        />
                        <Text color="fg.muted">
                          {stickyTabs.headerAction.locationText}
                        </Text>
                      </Flex>
                    </>
                  )}
                </Flex>

                <DetailPageButton
                  saleStartsOnPassed={saleStartsOnPassed}
                  eventData={rawData.event}
                  membershipGroupData={rawData.membership}
                  openModal={stickyTabs.cta.onClick}
                  props={{
                    "data-testid": "header-action-button",
                    colorPalette: "secondary",
                    minHeight: "40px",
                    minWidth: "147px",
                    display:
                      pageType === "event" &&
                      eventSaleStartsOn &&
                      timeRemaining > 0
                        ? "none"
                        : "block",
                  }}
                />
              </Flex>
            )}

            <Flex
              justifyContent="center"
              alignItems="center"
              height="element.4xl"
              bg="bg.muted"
              borderTop="1px solid"
              borderTopColor="border"
              gap="4xl"
              data-testid="share-links-container"
            >
              <HeaderShareLinks
                {...shareButtonsData}
                title={
                  <Text fontSize="md" color="fg.muted">
                    {t("Share")}
                  </Text>
                }
              />
            </Flex>
          </Flex>
        </GridItem>
      </Grid>
    </StickySection>
  );
}
